import {
  D7_TRIGGER_QA_SCENARIOS,
  type D7TriggerQaScenario,
} from "@/lib/hooks/admin/d7-trigger-qa-scenario";
import type {
  ActionSuggestionKind,
  ActionSuggestionSignals,
} from "./action/context/action-suggestion";
import type { AttentionBannerSignals } from "./attention/attention-banner";
import type { NoticeMentorshipSignals } from "./notice/notice-badge";

const FORCED_ENTITY_ID = "forced-d7-trigger-qa";

const EMPTY_NOTICE_SIGNALS: NoticeMentorshipSignals = {
  birthday: false,
  momentHubKind: null,
  dayState: null,
  momentum: false,
  opportunityActivityKind: null,
  checkIn: false,
  opportunityHub: false,
  riskOfPause: false,
};

const emptyActionSignals = (
  signals: ActionSuggestionSignals,
): ActionSuggestionSignals => ({
  ...signals,
  birthday: false,
  birthdayWeekendDay: null,
  activityPending: false,
  activityType: null,
  activityEntityId: null,
  hubContentPending: false,
  hubContentType: null,
  hubContentEntityId: null,
  opportunityActivity: null,
  checkInDue: false,
  opportunityHub: false,
  riskOfPause: false,
  endOfDay: false,
});

const emptyAttentionSignals = (
  signals: AttentionBannerSignals,
): AttentionBannerSignals => ({
  ...signals,
  dayState: null,
  day1Enabled: false,
  day2Enabled: false,
  day1Dismissed: false,
  day2Dismissed: false,
  day2Expired: false,
  reactivationActive: false,
  reactivationDismissed: false,
  momentumActive: false,
});

interface D7TriggerQaDefinition {
  label: string;
  expected: string;
  notice: Partial<NoticeMentorshipSignals> | null;
  attention: Partial<AttentionBannerSignals> | null;
  action: Partial<ActionSuggestionSignals>;
  forcedEntityAction?: boolean;
}

export const D7_TRIGGER_QA_DEFINITIONS = {
  birthday: {
    label: "Birthday",
    expected: "Birthday badge · birthday Action suggestion",
    notice: { birthday: true },
    attention: {},
    action: { birthday: true },
  },
  day1: {
    label: "Day 1",
    expected: "Day 1 badge · Day 1 banner",
    notice: { dayState: "day1" },
    attention: { dayState: "day1", day1Enabled: true },
    action: {},
  },
  day2: {
    label: "Day 2",
    expected: "Day 2 badge · Day 2 banner",
    notice: { dayState: "day2" },
    attention: { dayState: "day2", day2Enabled: true },
    action: {},
  },
  activity_completed: {
    label: "Completed activity",
    expected: "Needs response badge · completed-activity Action suggestion",
    notice: { momentHubKind: "activity" },
    attention: {},
    action: {
      activityPending: true,
      activityType: "hot_takes",
      activityEntityId: FORCED_ENTITY_ID,
    },
    forcedEntityAction: true,
  },
  hub_content_shared: {
    label: "Hub content shared",
    expected: "Needs response badge · Hub-content Action suggestion",
    notice: { momentHubKind: "content" },
    attention: {},
    action: {
      hubContentPending: true,
      hubContentType: "moment",
      hubContentEntityId: FORCED_ENTITY_ID,
    },
    forcedEntityAction: true,
  },
  momentum: {
    label: "Needs momentum",
    expected: "Momentum badge · momentum banner",
    notice: { momentum: true },
    attention: { momentumActive: true },
    action: {},
  },
  opportunity_hot_takes: {
    label: "Hot Takes opportunity",
    expected: "No list badge · Send Hot Takes Action suggestion",
    notice: { opportunityActivityKind: "hot_takes" },
    attention: {},
    action: { opportunityActivity: "hot_takes" },
  },
  opportunity_connect: {
    label: "Connection Cards opportunity",
    expected: "No list badge · Send Connection Cards Action suggestion",
    notice: { opportunityActivityKind: "connect_moment" },
    attention: {},
    action: { opportunityActivity: "connect_moment" },
  },
  opportunity_deep_thoughts: {
    label: "Deep Thoughts opportunity",
    expected: "No list badge · Send Deep Thoughts Action suggestion",
    notice: { opportunityActivityKind: "deep_thoughts" },
    attention: {},
    action: { opportunityActivity: "deep_thoughts" },
  },
  check_in: {
    label: "Check-in",
    expected: "Check-in badge · Start a Swap Action suggestion",
    notice: { checkIn: true },
    attention: {},
    action: { checkInDue: true },
  },
  opportunity_swap: {
    label: "Swap opportunity",
    expected: "No list badge · Start a Swap Action suggestion",
    notice: { opportunityHub: true },
    attention: {},
    action: { opportunityHub: true },
  },
  risk_of_pause: {
    label: "Risk of pause",
    expected: "Risk of pause badge · at-risk Action suggestion",
    notice: { riskOfPause: true },
    attention: {},
    action: { riskOfPause: true },
  },
  empty: {
    label: "Empty state",
    expected: "No badge · no banner · empty Action state",
    notice: {},
    attention: {},
    action: {},
  },
  end_of_day: {
    label: "End of day",
    expected: "Badge and banner unchanged · end-of-day Action suggestion",
    notice: null,
    attention: null,
    action: { endOfDay: true },
  },
} satisfies Record<D7TriggerQaScenario, D7TriggerQaDefinition>;

export const D7_TRIGGER_QA_OPTIONS = D7_TRIGGER_QA_SCENARIOS.map((value) => ({
  value,
  label: D7_TRIGGER_QA_DEFINITIONS[value].label,
  expected: D7_TRIGGER_QA_DEFINITIONS[value].expected,
}));

export function isForcedD7QaEntityScenario(
  scenario: D7TriggerQaScenario | null,
): boolean {
  if (!scenario) return false;
  const definition: D7TriggerQaDefinition =
    D7_TRIGGER_QA_DEFINITIONS[scenario];
  return definition.forcedEntityAction === true;
}

export function getD7QaDismissedActionKinds(
  scenario: D7TriggerQaScenario | null,
  liveDismissedKinds: ReadonlySet<ActionSuggestionKind>,
  qaDismissedKinds: ReadonlySet<ActionSuggestionKind>,
): ReadonlySet<ActionSuggestionKind> {
  return scenario ? qaDismissedKinds : liveDismissedKinds;
}

export function applyD7TriggerQaToNoticeSignals(
  signals: NoticeMentorshipSignals,
  scenario: D7TriggerQaScenario | null,
): NoticeMentorshipSignals {
  if (!scenario) return signals;
  const override = D7_TRIGGER_QA_DEFINITIONS[scenario].notice;
  return override ? { ...EMPTY_NOTICE_SIGNALS, ...override } : signals;
}

export function applyD7TriggerQaToActionSignals(
  signals: ActionSuggestionSignals,
  scenario: D7TriggerQaScenario | null,
): ActionSuggestionSignals {
  if (!scenario) return signals;
  return {
    ...emptyActionSignals(signals),
    ...D7_TRIGGER_QA_DEFINITIONS[scenario].action,
  };
}

export function applyD7TriggerQaToAttentionSignals(
  signals: AttentionBannerSignals,
  scenario: D7TriggerQaScenario | null,
): AttentionBannerSignals {
  if (!scenario) return signals;
  const override = D7_TRIGGER_QA_DEFINITIONS[scenario].attention;
  return override
    ? { ...emptyAttentionSignals(signals), ...override }
    : signals;
}
